export default {
  'servicesOverview.tag': 'Professional Services',
  'servicesOverview.title': 'Our Services',
  'servicesOverview.desc':
    'Financial and corporate advisory across major global jurisdictions, including offshore fund setup, financial licensing, overseas company formation, and M&A.',
  'servicesOverview.cta': 'Learn more',

  'servicesOverview.lines.overseasFund.label': 'Offshore Fund Setup',
  'servicesOverview.lines.overseasFund.desc':
    'Support for establishing offshore investment funds in Ireland, Luxembourg, Malta and other leading European jurisdictions, covering structuring, regulatory filings and ongoing compliance.',
  'servicesOverview.lines.overseasFund.tags.0': 'UCITS / AIF',
  'servicesOverview.lines.overseasFund.tags.1': 'EU Passport',
  'servicesOverview.lines.overseasFund.tags.2': 'Ongoing Compliance',

  'servicesOverview.lines.financialLicensing.label': 'Financial Licensing',
  'servicesOverview.lines.financialLicensing.desc':
    'End-to-end support for financial services licences in the UK, Ireland, Lithuania, Switzerland and more, including EMI, FX brokerage, payment and crypto-asset licences.',
  'servicesOverview.lines.financialLicensing.tags.0': 'EMI / Payments',
  'servicesOverview.lines.financialLicensing.tags.1': 'FX Brokerage',
  'servicesOverview.lines.financialLicensing.tags.2': 'Crypto Assets',

  'servicesOverview.lines.overseasCompany.label': 'Overseas Company Formation',
  'servicesOverview.lines.overseasCompany.desc':
    'Guidance on company registration in the UK, Ireland, Portugal, Lithuania, Malta and other jurisdictions, including registered address, banking and ongoing secretarial services.',
  'servicesOverview.lines.overseasCompany.tags.0': 'EU Registration',
  'servicesOverview.lines.overseasCompany.tags.1': 'Offshore Structures',
  'servicesOverview.lines.overseasCompany.tags.2': 'Secretarial',

  'servicesOverview.lines.mergersAcquisitions.label': 'Mergers & Acquisitions',
  'servicesOverview.lines.mergersAcquisitions.desc':
    'Strategic advisory for cross-border M&A, including acquisition of licensed entities, due diligence coordination and post-merger integration.',
  'servicesOverview.lines.mergersAcquisitions.tags.0': 'Licensed Targets',
  'servicesOverview.lines.mergersAcquisitions.tags.1': 'Due Diligence',
  'servicesOverview.lines.mergersAcquisitions.tags.2': 'Integration',

  'services.detail.overseas-fund-setup.title': 'Offshore Fund Setup',
  'services.detail.overseas-fund-setup.summary':
    'Support for establishing offshore investment funds in Ireland, Luxembourg, Malta and other leading European jurisdictions, covering structuring, regulatory filings and ongoing compliance.',
  'services.detail.overseas-fund-setup.content': `## Fund Types

In the EU (including the UK), there are two main fund structures:

- **UCITS (Undertakings for Collective Investment in Transferable Securities)** — Primarily for retail investors, relatively liquid and typically open-ended. May invest in transferable securities such as equities and bonds. UCITS-compliant funds benefit from the EU passport for distribution across EU markets.
- **Alternative Investment Funds (AIF)** — Primarily for experienced professional investors. Includes real estate, private equity, hedge funds, fund-of-funds, commodity, infrastructure and non-UCITS debt funds. AIFMD-compliant funds may also benefit from the EU passport.

## Key Considerations When Establishing a Fund

- Target investors and fund size
- Investment strategy, asset types and domicile
- Distribution strategy
- Setup and ongoing maintenance costs
- Fund type and choice between single fund or umbrella structure
- Potential tax benefits
- Fund operations ecosystem

## Service Providers and Typical Fees

- **Local directors** — Relevant industry experience required; fees vary by jurisdiction
- **Licensed fund manager (AIFM/Manco)** — Typically 0.3% to 1% of net assets
- **Depositary** — Typically 0.05% to 0.10% of net assets, with possible annual minimums
- **Fund administrator** — Typically 0.10% to 0.30% of net assets
- **Fund auditor** — Based on size and complexity
- **Fund legal counsel** — Based on operational needs

## Our Services

- Recommend suitable fund structures based on business model, scale and asset types
- Incorporate fund entities locally (private company, trust or open-ended investment company, etc.)
- Build professional management teams including licensed individuals, compliance officers and directors
- Support legal drafting of prospectus, subscription documents, sub-fund supplements, business plans and budgets
- Introduce regulated service providers and review contracts on your behalf
- Submit fund authorisation applications to regulators
- Respond to regulator queries and attend interviews during review

## Regulators We Work With

- UK Financial Conduct Authority (FCA)
- Central Bank of Ireland
- Bank of Lithuania
- Banco de Portugal`,

  'services.detail.financial-licensing.title': 'Financial Licensing',
  'services.detail.financial-licensing.summary':
    'End-to-end support for financial services licences in the UK, Ireland, Lithuania, Switzerland and more, including EMI, FX brokerage, payment, virtual asset and ICO licences.',
  'services.detail.financial-licensing.content': `## Licence Types

### FX Brokerage Licences

UK FX licences fall into three main categories:

- **Market Maker (MM)** — Proprietary trading platform; may hold client funds and trade on own account. Minimum capital: EUR 730,000.
- **STP (Straight Through Processing)** — Intermediary matching trades; may hold client funds but no proprietary trading. Minimum capital: EUR 125,000.
- **Introducing Broker** — Introduces clients to licensed firms; cannot hold client funds. Minimum capital: EUR 50,000.

### Electronic Money Institution (EMI)

Beyond payment licence activities, EMIs may offer payment accounts, debit/credit cards, consumer lending, stablecoin issuance and P2P services (with additional authorisation). Minimum capital: EUR 350,000. Typical approval: 4–5 months.

### Payment Licences

International payments and transfers for businesses and individuals, including payment accounts, third-party payments, merchant acquiring and remittance. Minimum capital varies by scope (EUR 20k–125k). Approval: 6–12 months.

### Trust Company Licence (Jersey)

Two forms: **managed trust company** (attached to an existing licensee, lower barrier) or **full trust company** (global trust management, more complex). Approval: 6–8 months. Full Jersey presence required with at least 5 directors (majority local), separate compliance and AML officers.

### Virtual Asset / Crypto Licences

Support for crypto licences in Poland, BVI, Cayman Islands, Malta and more, including EU MiCAR CASP applications.

### Consumer Credit (UK)

Limited permission (hire purchase, ancillary credit broking) or full permission (all credit types, debt management, P2P). No minimum capital except for debt management. Applied via FCA Connect.

### Consumer Credit (Lithuania & Estonia)

Lithuania: 2–3 months when documentation is complete; may combine with EMI/payment for EU passporting. Estonia: issued by Finantsinspektsioon, no expiry, covers creditor and mortgage creditor categories; 6–8 months.

## Our Services

- Recommend the most suitable licence type for your business model
- Local company incorporation
- Pre-application materials, questionnaires and interview support
- Application forms, business plans, operational plans and budgets
- Build management teams including licensed individuals, compliance officers and directors
- IT systems development support
- Post-submission regulator query responses

## Regulators We Work With

- UK FCA
- Central Bank of Ireland
- Bank of Lithuania
- Banco de Portugal
- Swiss FINMA / SRO
- Jersey Financial Services Commission (JFSC)
- EU and offshore regulators`,

  'services.detail.overseas-company.title': 'Overseas Company Formation',
  'services.detail.overseas-company.summary':
    'Guidance on company registration in the UK, Ireland, Portugal, Lithuania, Malta and other jurisdictions, including registered address, banking and ongoing secretarial services.',
  'services.detail.overseas-company.content': `## Key Jurisdictions

We assist with incorporation in:

- **Europe:** Ireland, UK, Portugal, Lithuania, Malta, Switzerland, Netherlands, Spain, Hungary, Isle of Man
- **Offshore:** BVI, Cayman Islands, Dominica, Grenada, St Lucia, Mauritius (GBC1)
- **Other:** Nigeria, Serbia and more

## Why Incorporate Overseas?

- Access to specific regulatory status and EU passporting rights
- Tax optimisation and asset protection
- Enhanced international credibility and market access
- Holding structures for investments or intellectual property

## Our Services

- Company name search and reservation
- Preparation and filing of registration documents
- Registered office address
- Introduction of local directors and company secretary
- Bank account opening assistance
- Ongoing secretarial and compliance services
- Substance requirements support`,

  'services.detail.mergers-acquisitions.title': 'Mergers & Acquisitions',
  'services.detail.mergers-acquisitions.summary':
    'Strategic advisory for cross-border M&A, including acquisition of licensed entities, due diligence coordination and post-merger integration.',
  'services.detail.mergers-acquisitions.content': `## M&A Advisory

End-to-end support for cross-border M&A and licensed-entity transactions across Europe and major global jurisdictions.

## Available Licensed Assets

We maintain a pipeline of licensable entities for acquisition, including:

- Licensed investment firms (e.g. Luxembourg MiFID firms)
- EMI and payment institution licence holders (Netherlands, Poland)
- Full banking licences (Comoros Anjouan, Puerto Rico)
- Licensed crypto exchanges with payment licences (Poland)
- Portuguese and other EU banking licences

## Our Process

- Initial screening and target identification
- Preliminary due diligence and regulatory status review
- Structuring advice and transaction negotiation support
- Coordination of legal, compliance and financial due diligence
- Regulatory change-of-control filings and approval support
- Post-merger integration planning

## Why Acquire a Licensed Entity?

- Faster market entry than applying for a new licence from scratch
- Immediate operational capability
- Existing regulatory relationships and compliance infrastructure`,

  'seo.services.title': 'Services',
  'seo.services.desc':
    'Offshore fund setup, financial licensing, overseas company formation and M&A advisory across major jurisdictions.',
  'seo.services.keywords':
    'GEEL TEC, services, offshore fund, financial licence, company formation, M&A, cross-border advisory',

  'seo.services.overseas-fund-setup.title': 'Offshore Fund Setup',
  'seo.services.overseas-fund-setup.desc':
    'Fund establishment in Ireland, Luxembourg, Malta and more — UCITS/AIF structuring, regulatory filings and ongoing compliance.',
  'seo.services.overseas-fund-setup.keywords':
    'GEEL TEC, offshore fund, UCITS, AIF, fund setup, EU fund, fund compliance',

  'seo.services.financial-licensing.title': 'Financial Licensing',
  'seo.services.financial-licensing.desc':
    'EMI, payment, FX brokerage and crypto-asset licence applications in the UK, Ireland, Lithuania, Switzerland and more.',
  'seo.services.financial-licensing.keywords':
    'GEEL TEC, financial licence, EMI, payment licence, FX licence, crypto licence, FCA',

  'seo.services.overseas-company.title': 'Overseas Company Formation',
  'seo.services.overseas-company.desc':
    'Company registration, secretarial services, banking and substance compliance in European and offshore jurisdictions.',
  'seo.services.overseas-company.keywords':
    'GEEL TEC, overseas company, incorporation, offshore company, secretarial, bank account',

  'seo.services.mergers-acquisitions.title': 'Mergers & Acquisitions',
  'seo.services.mergers-acquisitions.desc':
    'Cross-border M&A and licensed-entity acquisitions — screening, due diligence, regulatory filings and integration.',
  'seo.services.mergers-acquisitions.keywords':
    'GEEL TEC, M&A, licensed acquisition, due diligence, cross-border M&A, integration',
};
